import React from 'react';

import {DataFrame, DataFrameColumns} from 'data-frames/DataFrameTypes';
import getUserColumns from 'data-frames/util/getUserColumns';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

export interface DataFrameRowsBannerProps {
  allColumnsJson: DataFrameColumns;
  dataFrame: DataFrame;
  isRandomSubset: boolean;
  name?: string;
  totalRows: number;
  /**
   * Whether the user has an option to download the complete data.
   *
   * @default true
   */
  canDownloadCompleteData?: boolean;
}

/**
 * Banner to alert the user when a data frame has more rows or columns than are currently being displayed.
 *
 * If the entire data frame is available, will return null.
 */
export default function DataFrameWarningBanner({
  allColumnsJson,
  dataFrame,
  isRandomSubset,
  name,
  totalRows,
  canDownloadCompleteData = true,
}: DataFrameRowsBannerProps): JSX.Element | null {
  const label = name ?? dataFrame.name;
  const messages: Array<InlineBannerProps> = [];
  if (dataFrame.rows.length < totalRows) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      text: `Showing ${isRandomSubset ? 'a random sample of' : 'the first'} ${
        dataFrame.rows.length
      } rows out of ${totalRows} from ${label}`,
      supportingInfo: canDownloadCompleteData ? 'Download to view the complete data.' : null,
    });
  }
  const availableColumns = getUserColumns(dataFrame).length;
  const totalColumns = getUserColumns({columns: allColumnsJson}).length;
  if (availableColumns < totalColumns) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      text: `Showing the first ${availableColumns} columns out of ${totalColumns} from ${label}`,
      supportingInfo: canDownloadCompleteData ? 'Download to view the complete data.' : null,
    });
  }
  return messages.length > 0 ? <InlineBannerStack messages={messages} /> : null;
}
